import React, { useEffect, useState } from 'react';
import Card from '../../components/ui/Card';
import Btn from '../../components/ui/Btn';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';
import { Input, Select } from '../../components/ui/Input';
import { contasPagarApi } from '../../api/financeiro';
import { fornecedoresApi } from '../../api/fornecedores';
import { COLORS } from '../../theme';
import { useTituloPagina } from '../../context/PageTitleContext';

const FORMA_LABEL = { boleto: 'Boleto', pix: 'Pix', conta: 'Crédito em Conta' };
const STATUS_TOM = { pendente: 'aviso', pago: 'sucesso', cancelado: 'perigo' };
const STATUS_LABEL = { pendente: 'Pendente', pago: 'Pago', cancelado: 'Cancelado' };
const CATEGORIA_LABEL = {
  compra_mercadorias: 'Compra de Mercadorias', impostos: 'Impostos', multas: 'Multas',
  custo_fixo: 'Custo Fixo', servicos_fornecedores: 'Serviços/Fornecedores', marketing: 'Marketing', outros: 'Outros'
};
const OPCOES_CATEGORIA = [
  { value: 'compra_mercadorias', label: 'Compra de Mercadorias' },
  { value: 'impostos', label: 'Impostos' },
  { value: 'multas', label: 'Multas' },
  { value: 'custo_fixo', label: 'Custo Fixo' },
  { value: 'servicos_fornecedores', label: 'Serviços/Fornecedores' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'outros', label: 'Outros' }
];

function formatarReais(v) {
  return (Number(v) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function estaAtrasada(conta) {
  return conta.status === 'pendente' && conta.data_vencimento < new Date().toISOString().slice(0, 10);
}

const FORM_VAZIO = {
  descricao: '', categoria: 'compra_mercadorias', fornecedor_id: '', forma_pagamento: 'boleto',
  valor: '', data_compra: new Date().toISOString().slice(0, 10), data_vencimento: '', observacoes: '',
  recorrente: false, recorrencia_meses: '12'
};

export default function ContasPagarList() {
  useTituloPagina('Contas a Pagar');
  const [contas, setContas] = useState([]);
  const [fornecedores, setFornecedores] = useState([]);

  const [filtroStatus, setFiltroStatus] = useState('pendente');
  const [filtroForma, setFiltroForma] = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [filtroFornecedor, setFiltroFornecedor] = useState('');
  const [filtroOrigem, setFiltroOrigem] = useState('');
  const [filtroDataDe, setFiltroDataDe] = useState('');
  const [filtroDataAte, setFiltroDataAte] = useState('');
  const [filtroBusca, setFiltroBusca] = useState('');

  const [modalAberto, setModalAberto] = useState(false);
  const [form, setForm] = useState(FORM_VAZIO);
  const [prazosBoleto, setPrazosBoleto] = useState(['30']);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  const carregar = () => {
    const params = {};
    if (filtroStatus) params.status = filtroStatus;
    if (filtroForma) params.forma_pagamento = filtroForma;
    if (filtroCategoria) params.categoria = filtroCategoria;
    if (filtroFornecedor) params.fornecedor_id = filtroFornecedor;
    if (filtroOrigem) params.origem = filtroOrigem;
    if (filtroDataDe) params.data_de = filtroDataDe;
    if (filtroDataAte) params.data_ate = filtroDataAte;
    if (filtroBusca) params.busca = filtroBusca;
    contasPagarApi.listar(params).then(setContas).catch(() => {});
  };

  useEffect(() => { carregar(); }, [filtroStatus, filtroForma, filtroCategoria, filtroFornecedor, filtroOrigem, filtroDataDe, filtroDataAte, filtroBusca]); // eslint-disable-line
  useEffect(() => { fornecedoresApi.listar().then(setFornecedores).catch(() => {}); }, []);

  const totalListado = contas.reduce((s, c) => s + Number(c.valor), 0);
  const opcoesFornecedores = [{ value: '', label: '(sem fornecedor)' }, ...fornecedores.map(f => ({ value: f.id, label: f.nome_fantasia }))];
  const opcoesFornecedoresFiltro = [{ value: '', label: 'Todos os fornecedores' }, ...fornecedores.map(f => ({ value: f.id, label: f.nome_fantasia }))];

  const abrirNovo = () => { setForm(FORM_VAZIO); setPrazosBoleto(['30']); setErro(''); setModalAberto(true); };

  const limparFiltros = () => {
    setFiltroStatus(''); setFiltroForma(''); setFiltroCategoria(''); setFiltroFornecedor('');
    setFiltroOrigem(''); setFiltroDataDe(''); setFiltroDataAte(''); setFiltroBusca('');
  };

  const alterarPrazoBoleto = (idx, valor) => setPrazosBoleto(p => p.map((v, i) => i === idx ? valor : v));
  const adicionarPrazoBoleto = () => setPrazosBoleto(p => [...p, '']);
  const removerPrazoBoleto = (idx) => setPrazosBoleto(p => p.filter((_, i) => i !== idx));

  const salvar = async (e) => {
    e.preventDefault();
    setErro('');
    const payload = {
      descricao: form.descricao, categoria: form.categoria, fornecedor_id: form.fornecedor_id || null,
      forma_pagamento: form.forma_pagamento, valor: form.valor, observacoes: form.observacoes
    };

    if (form.forma_pagamento === 'boleto') {
      const dias = prazosBoleto.map(d => d.trim()).filter(Boolean);
      if (dias.length === 0 || !dias.every(d => /^\d+$/.test(d))) {
        setErro('Preencha os prazos das parcelas com números de dias válidos.');
        return;
      }
      if (!form.data_compra) { setErro('Informe a data da compra.'); return; }
      payload.data_compra = form.data_compra;
      payload.parcelas_dias = dias.map(Number);
      if (dias.length === 1 && form.recorrente) {
        payload.recorrente = true;
        payload.recorrencia_meses = Number(form.recorrencia_meses) || 12;
      }
    } else {
      if (!form.data_vencimento) { setErro('Informe a data de vencimento.'); return; }
      payload.data_compra = form.data_compra || null;
      payload.data_vencimento = form.data_vencimento;
      if (form.recorrente) {
        payload.recorrente = true;
        payload.recorrencia_meses = Number(form.recorrencia_meses) || 12;
      }
    }

    setSalvando(true);
    try {
      await contasPagarApi.criar(payload);
      setModalAberto(false);
      carregar();
    } catch (err) {
      setErro(err.message);
    } finally {
      setSalvando(false);
    }
  };

  const marcarPago = async (conta) => {
    if (!window.confirm(`Confirmar pagamento de ${formatarReais(conta.valor)} — ${conta.descricao}?`)) return;
    await contasPagarApi.pagar(conta.id);
    carregar();
  };

  const cancelar = async (conta) => {
    if (!window.confirm(`Cancelar o lançamento "${conta.descricao}"?`)) return;
    await contasPagarApi.cancelar(conta.id);
    carregar();
  };

  return (
    <div>
      <Card style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 12, marginBottom: 12 }}>
          <strong style={{ fontSize: 13 }}>Filtros</strong>
          <Btn onClick={abrirNovo}>+ Novo Lançamento</Btn>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 10 }}>
          <Select label="Status" options={[{ value: '', label: 'Todos' }, { value: 'pendente', label: 'Pendente' }, { value: 'pago', label: 'Pago' }, { value: 'cancelado', label: 'Cancelado' }]}
            value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)} />
          <Select label="Forma de Pagamento" options={[{ value: '', label: 'Todas' }, { value: 'boleto', label: 'Boleto' }, { value: 'pix', label: 'Pix' }, { value: 'conta', label: 'Crédito em Conta' }]}
            value={filtroForma} onChange={e => setFiltroForma(e.target.value)} />
          <Select label="Categoria" options={[{ value: '', label: 'Todas' }, ...OPCOES_CATEGORIA]}
            value={filtroCategoria} onChange={e => setFiltroCategoria(e.target.value)} />
          <Select label="Origem" options={[{ value: '', label: 'Todas' }, { value: 'nota_entrada', label: 'Nota Fiscal de Entrada' }, { value: 'manual', label: 'Lançamento Manual' }]}
            value={filtroOrigem} onChange={e => setFiltroOrigem(e.target.value)} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr 1fr auto', gap: 10, alignItems: 'end' }}>
          <Select label="Fornecedor" options={opcoesFornecedoresFiltro} value={filtroFornecedor} onChange={e => setFiltroFornecedor(e.target.value)} />
          <Input label="Vencimento De" type="date" value={filtroDataDe} onChange={e => setFiltroDataDe(e.target.value)} />
          <Input label="Vencimento Até" type="date" value={filtroDataAte} onChange={e => setFiltroDataAte(e.target.value)} />
          <Input label="Buscar Descrição" value={filtroBusca} onChange={e => setFiltroBusca(e.target.value)} placeholder="Ex: aluguel..." />
          <Btn variante="ghost" onClick={limparFiltros}>Limpar</Btn>
        </div>
      </Card>

      <Card style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ background: COLORS.primaryBg, borderRadius: 10, padding: 14, flex: 1 }}>
            <div style={{ fontSize: 11, color: COLORS.primary, textTransform: 'uppercase', fontWeight: 700 }}>Total Listado</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: COLORS.primary }}>{formatarReais(totalListado)}</div>
          </div>
          <div style={{ background: COLORS.bg, borderRadius: 10, padding: 14, flex: 1 }}>
            <div style={{ fontSize: 11, color: COLORS.textLight, textTransform: 'uppercase', fontWeight: 700 }}>Lançamentos</div>
            <div style={{ fontSize: 20, fontWeight: 700 }}>{contas.length}</div>
          </div>
        </div>
      </Card>

      <Card style={{ padding: 0, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: 'left', background: COLORS.bg }}>
              <th style={{ padding: 10 }}>Vencimento</th>
              <th style={{ padding: 10 }}>Descrição</th>
              <th style={{ padding: 10 }}>Categoria</th>
              <th style={{ padding: 10 }}>Fornecedor</th>
              <th style={{ padding: 10 }}>Forma</th>
              <th style={{ padding: 10 }}>Valor</th>
              <th style={{ padding: 10 }}>Status</th>
              <th style={{ padding: 10 }} />
            </tr>
          </thead>
          <tbody>
            {contas.map(c => (
              <tr key={c.id} style={{ borderTop: `1px solid ${COLORS.border}` }}>
                <td style={{ padding: 10, color: estaAtrasada(c) ? COLORS.danger : COLORS.text, fontWeight: estaAtrasada(c) ? 700 : 400 }}>
                  {new Date(c.data_vencimento + 'T00:00:00').toLocaleDateString('pt-BR')}{estaAtrasada(c) ? ' (atrasada)' : ''}
                </td>
                <td style={{ padding: 10 }}>
                  {c.descricao}
                  {c.nota_entrada_numero && <Badge tom="info">NF {c.nota_entrada_numero}</Badge>}
                </td>
                <td style={{ padding: 10 }}>{CATEGORIA_LABEL[c.categoria] || '-'}</td>
                <td style={{ padding: 10 }}>{c.fornecedor_nome || '-'}</td>
                <td style={{ padding: 10 }}>{FORMA_LABEL[c.forma_pagamento]}</td>
                <td style={{ padding: 10, fontWeight: 600 }}>{formatarReais(c.valor)}</td>
                <td style={{ padding: 10 }}><Badge tom={STATUS_TOM[c.status]}>{STATUS_LABEL[c.status]}</Badge></td>
                <td style={{ padding: 10 }}>
                  {c.status === 'pendente' && (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <Btn tamanho="sm" onClick={() => marcarPago(c)}>Pagar</Btn>
                      <Btn tamanho="sm" variante="danger" onClick={() => cancelar(c)}>Cancelar</Btn>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {contas.length === 0 && (
              <tr><td colSpan={8} style={{ padding: 20, textAlign: 'center', color: COLORS.textMid }}>Nenhum lançamento encontrado.</td></tr>
            )}
          </tbody>
        </table>
      </Card>

      {modalAberto && (
        <Modal titulo="Novo Lançamento" onFechar={() => setModalAberto(false)} largura={560}
          rodape={<>
            <Btn variante="ghost" onClick={() => setModalAberto(false)}>Cancelar</Btn>
            <Btn onClick={salvar} disabled={salvando}>{salvando ? 'Salvando...' : 'Salvar'}</Btn>
          </>}>
          <form onSubmit={salvar} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Input label="Descrição" value={form.descricao} onChange={e => setForm(f => ({ ...f, descricao: e.target.value }))} required />

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <Select label="Categoria" options={OPCOES_CATEGORIA} value={form.categoria} onChange={e => setForm(f => ({ ...f, categoria: e.target.value }))} />
              <Select label="Fornecedor (opcional)" options={opcoesFornecedores} value={form.fornecedor_id} onChange={e => setForm(f => ({ ...f, fornecedor_id: e.target.value }))} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <Select label="Forma de Pagamento" options={[{ value: 'boleto', label: 'Boleto' }, { value: 'pix', label: 'Pix' }, { value: 'conta', label: 'Crédito em Conta' }]}
                value={form.forma_pagamento} onChange={e => setForm(f => ({ ...f, forma_pagamento: e.target.value }))} />
              <Input label="Valor Total R$" type="number" step="0.01" value={form.valor} onChange={e => setForm(f => ({ ...f, valor: e.target.value }))} required />
            </div>

            <Input label="Data da Compra" type="date" value={form.data_compra} onChange={e => setForm(f => ({ ...f, data_compra: e.target.value }))} />

            {form.forma_pagamento === 'boleto' ? (
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMid, marginBottom: 6 }}>
                  Parcelas (dias após a data da compra)
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {prazosBoleto.map((prazo, idx) => (
                    <div key={idx} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <Input type="number" min="0" value={prazo} onChange={e => alterarPrazoBoleto(idx, e.target.value)}
                        placeholder="Ex: 30" style={{ width: 120 }} />
                      <span style={{ fontSize: 12, color: COLORS.textLight }}>dias — vencimento calculado automaticamente</span>
                      {prazosBoleto.length > 1 && (
                        <Btn tamanho="sm" variante="danger" type="button" onClick={() => removerPrazoBoleto(idx)}>Remover</Btn>
                      )}
                    </div>
                  ))}
                </div>
                <Btn tamanho="sm" variante="secondary" type="button" onClick={adicionarPrazoBoleto} style={{ marginTop: 8 }}>
                  + Adicionar Parcela
                </Btn>
                <p style={{ fontSize: 11, color: COLORS.textLight, marginTop: 8 }}>
                  O valor total é dividido igualmente entre as parcelas (a última ajusta os centavos).
                </p>
              </div>
            ) : (
              <Input label="Data de Vencimento" type="date" value={form.data_vencimento} onChange={e => setForm(f => ({ ...f, data_vencimento: e.target.value }))} required />
            )}

            {(form.forma_pagamento !== 'boleto' || prazosBoleto.length === 1) && (
              <div style={{ background: COLORS.bg, borderRadius: 8, padding: 12 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.recorrente} onChange={e => setForm(f => ({ ...f, recorrente: e.target.checked }))} />
                  Lançamento recorrente (ex: multa, custo fixo mensal)
                </label>
                {form.recorrente && (
                  <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Input type="number" min="1" max="36" value={form.recorrencia_meses}
                      onChange={e => setForm(f => ({ ...f, recorrencia_meses: e.target.value }))} style={{ width: 90 }} />
                    <span style={{ fontSize: 12, color: COLORS.textLight }}>meses seguidos, repetindo no mesmo dia do mês</span>
                  </div>
                )}
              </div>
            )}

            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMid, marginBottom: 4 }}>Observações</div>
              <textarea value={form.observacoes} onChange={e => setForm(f => ({ ...f, observacoes: e.target.value }))} rows={2}
                style={{ width: '100%', border: `1.5px solid ${COLORS.border}`, borderRadius: 7, padding: '8px 9px', fontSize: 13, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }} />
            </div>
            {erro && <p style={{ color: COLORS.danger, fontSize: 13, margin: 0 }}>{erro}</p>}
          </form>
        </Modal>
      )}
    </div>
  );
}
